import {
  Flex, Input, Table, Typography,
} from '@duxton-web/ui';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

// Fetch-on-mount + map a content-config list out of redux. Returns the mapped
// FE rows, the raw API rows (for pages that derive extra state), and a loading
// flag that is only true during the first load (so refreshes don't blank the
// table). `arg` is forwarded to the fetch action creator.
export const useEntityList = (action, statePath, requestPath, mapper, arg) => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(action(arg));
  }, [action, arg, dispatch]);
  const raw = useSelector((s) => _.get(s, statePath, EMPTY_ARRAY));
  const req = useSelector((s) => _.get(s, requestPath, EMPTY_OBJECT));
  const data = useMemo(() => raw.map(mapper), [mapper, raw]);
  return { data, raw, loading: Boolean(req.isPending) && data.length === 0 };
};

const rowMatches = (row, fields, q) => fields.some(
  (f) => String(row[f] || '').toLowerCase().includes(q),
);

// Shared scaffold for the read-only content-config list pages: a title, an
// optional search box with a "X of Y" count, and a Duxton Table with consistent
// loading / empty / row-click behaviour. Pass a Drawer/Modal as children to
// render below the table.
const EntityListPage = ({
  title,
  columns,
  data,
  rowKey,
  loading = false,
  emptyText = 'Nothing here yet.',
  searchable = false,
  searchPlaceholder = 'Search…',
  searchFields = [],
  searchEmptyText,
  onRowClick,
  children,
}) => {
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!searchable || !q) return data;
    return data.filter((row) => rowMatches(row, searchFields, q));
  }, [data, q, searchable, searchFields]);

  const empty = q && filtered.length === 0 && searchEmptyText
    ? searchEmptyText(search)
    : emptyText;

  return (
    <Flex vertical gap="middle">
      <Typography.Title level={4}>{title}</Typography.Title>

      {searchable && (
        <Flex align="center" gap="middle" wrap="wrap">
          <Input
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            className="cc-search-input"
          />
          <Typography.Metadata type="secondary">
            {filtered.length} of {data.length}
          </Typography.Metadata>
        </Flex>
      )}

      <Table
        rowKey={rowKey}
        columns={columns}
        dataSource={filtered}
        loading={loading}
        pagination={false}
        onRow={onRowClick ? (record) => ({
          onClick: () => onRowClick(record),
          className: 'cc-row-clickable',
        }) : undefined}
        locale={{ emptyText: empty }}
      />

      {children}
    </Flex>
  );
};

export default EntityListPage;
